import { useMemo } from "react";
import { useChainData } from "../../../../components/chain-data-provider";
import { usePropertyInventoryData } from "../../../../components/inventory-provider";
import { usePropertyData } from "../../../../components/property-admin-data-provider";
import { InventoryEntry } from "../../../../lib/model";

export function useInventory(filter?: (inventory: InventoryEntry) => boolean) {
  const chainData = useChainData();
  const propertyData = usePropertyData();
  const propertyInventoryData = usePropertyInventoryData();

  const isChain = useMemo(() => chainData?.isChain, [chainData]);

  const allowedInventoryLines = useMemo(() => {
    if (!propertyData?.inventories?.length) {
      return [];
    }

    const inventories = filter ? propertyData.inventories.filter(filter) : propertyData.inventories;
    return inventories.map(({ ticker, accommodation, configuration, mealPlan, condition }) => {
      return {
        ticker,
        name: `${accommodation.name} / ${configuration.name}, ${mealPlan.name}, ${condition.name}`,
        filters: { accommodation, condition, configuration, mealPlan },
      };
    });
  }, [filter, propertyData?.inventories]);

  const allowedChainInventoryLines = useMemo(() => {
    if (!isChain || !propertyInventoryData || !propertyInventoryData.length) {
      return [];
    }

    return propertyInventoryData.map(({ propertyTicker, inventoryTickers }) => {
      const inventories = filter ? inventoryTickers?.filter(filter) : inventoryTickers;
      return {
        [propertyTicker]: inventories?.map(
          ({ ticker, accommodation, configuration, mealPlan, condition }) => {
            return {
              ticker,
              name: `${accommodation.name} / ${configuration.name}, ${mealPlan.name}, ${condition.name}`,
              filters: { accommodation, condition, configuration, mealPlan },
            };
          }
        ),
      };
    });
  }, [filter, isChain, propertyInventoryData]);

  const allowedChainInventoryLinesOptions = useMemo(() => {
    return allowedChainInventoryLines.map(it => Object.values(it)[0]);
  }, [allowedChainInventoryLines]);

  const allowedChainInventoryLinesTickers = useMemo(() => {
    return allowedChainInventoryLines.map(it => Object.keys(it)[0]);
  }, [allowedChainInventoryLines]);

  const loadChainInventoryLinesAllowed = useMemo(() => {
    return (
      isChain &&
      allowedChainInventoryLines.length > 0 &&
      allowedChainInventoryLinesOptions.length > 0 &&
      allowedChainInventoryLinesTickers.length > 0
    );
  }, [
    allowedChainInventoryLines.length,
    allowedChainInventoryLinesOptions.length,
    allowedChainInventoryLinesTickers.length,
    isChain,
  ]);

  const loadInventoryLinesAllowed = useMemo(() => {
    return allowedInventoryLines.length > 0;
  }, [allowedInventoryLines.length]);

  return {
    allowedInventoryLines,
    allowedChainInventoryLines,
    allowedChainInventoryLinesOptions,
    allowedChainInventoryLinesTickers,
    loadChainInventoryLinesAllowed,
    loadInventoryLinesAllowed,
  };
}
